// src/Hooks/useEntUpload.js
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import API_BASE_URL from "../config";

const ENT_UPLOAD_ENDPOINT = "/ent/entUpload";

const dataUrlToBlob = async (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") {
    return null;
  }

  try {
    const response = await fetch(dataUrl);
    return await response.blob();
  } catch (error) {
    console.error("Failed to convert ENT image to blob", error);
    return null;
  }
};

export function useEntUpload() {
  const token = useSelector((state) => state?.authSlice?.user?.token);
  const patientId = useSelector(
    (state) => state?.patientSlice?.patientData?.PatientID
  );

  const [entUploading, setEntUploading] = useState(false);

  const uploadEntImages = useCallback(
    async (images = [], overrides = {}) => {
      const validImages = Array.isArray(images)
        ? images.filter((img) => typeof img === "string" && img.trim() !== "")
        : [];

      const targetPatientId = overrides?.patientId || patientId;

      if (!validImages.length) {
        return null;
      }

      if (!targetPatientId) {
        toast.error("Patient ID not found. Please select a patient before saving.", {
          position: toast.POSITION.TOP_CENTER,
        });
        throw new Error("Missing patient ID for ENT upload");
      }

      if (!token) {
        toast.error("You are not authorized to upload ENT images.", {
          position: toast.POSITION.TOP_CENTER,
        });
        throw new Error("Missing auth token for ENT upload");
      }

      setEntUploading(true);

      try {
        const formData = new FormData();
        const blobs = await Promise.all(validImages.map(dataUrlToBlob));

        blobs.forEach((blob, index) => {
          if (!blob) return;
          const extension = blob.type?.split("/")[1] || "png";
          const filename = `ent-image-${index + 1}.${extension}`;
          formData.append("file", blob, filename);
        });

        if (!formData.has("file")) {
          toast.error("Unable to prepare ENT images for upload.", {
            position: toast.POSITION.TOP_CENTER,
          });
          throw new Error("No ENT blobs appended");
        }

        const url = `${API_BASE_URL}${ENT_UPLOAD_ENDPOINT}?PatientID=${encodeURIComponent(
          targetPatientId
        )}`;

        const response = await axios.post(url, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        toast.success("ENT images uploaded successfully.", {
          position: toast.POSITION.TOP_CENTER,
        });

        return response?.data;
      } catch (error) {
        console.error("ENT upload failed", error);
        toast.error("Failed to upload ENT images. Please try again.", {
          position: toast.POSITION.TOP_CENTER,
        });
        throw error;
      } finally {
        setEntUploading(false);
      }
    },
    [patientId, token]
  );

  return {
    uploadEntImages,
    entUploading,
  };
}

export default useEntUpload;
